import { getAuthToken } from '../../utils/auth';
import {
  searchReportingFetchJSON as safeFetchJSON,
  searchReportingLegacyUrl,
} from '../../lib/flags/search-reporting';

const OPENSEARCH_URL = searchReportingLegacyUrl;
if (!OPENSEARCH_URL) {
  console.error('[UniversalSearch] The legacy search URL is not set — search will be disabled');
}

const FETCH_CONFIG = { retries: 2, retryDelay: 1000 };

export async function searchContacts(merchants, keyword) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/contacts`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'topContacts',
      filters: {
        merchantNames: merchants,
        keyword,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
      },
    }),
    ...FETCH_CONFIG,
    context: 'searchContacts',
  });
  return result.data || [];
}

/**
 * searchContacts that says when the search failed: it throws on an error answer
 * or no answer, where searchContacts returns [] (its other callers rely on that).
 * For the segment people picker, so a failed search never reads "No one matches".
 */
export async function searchContactsStrict(merchants, keyword) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/contacts`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'topContacts',
      filters: {
        merchantNames: merchants,
        keyword,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
      },
    }),
    ...FETCH_CONFIG,
    context: 'searchContactsStrict',
  });
  if (!result.ok) throw new Error(result.error || 'Contact search failed');
  return result.data || [];
}

export async function searchTransactionDonors(merchants, keyword) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/transactions`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'topContacts',
      filters: {
        merchantNames: merchants,
        keyword,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
        startDate: '',
        endDate: '',
        paymentMethods: '',
        states: [],
        frequencies: [],
        formTypes: [],
        amountType: 'AMOUNT',
      },
    }),
    ...FETCH_CONFIG,
    context: 'searchTransactionDonors',
  });
  return result.data || [];
}

export async function searchSubscriptionDonors(merchants, keyword) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/subscriptions`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'topContacts',
      filters: {
        merchantNames: merchants,
        keyword,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
        startDate: '',
        endDate: '',
        frequencies: '',
        states: [],
        amountType: 'AMOUNT',
        dateType: '',
      },
    }),
    ...FETCH_CONFIG,
    context: 'searchSubscriptionDonors',
  });
  return result.data || [];
}

export async function searchForms(merchants, keyword) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/forms`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'topForms',
      filters: {
        merchantNames: merchants,
        keyword,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
        types: [],
        statuses: [],
      },
    }),
    ...FETCH_CONFIG,
    context: 'searchForms',
  });
  return result.data || [];
}

export async function searchElements(merchants, keyword) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/elements`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'topElements',
      filters: {
        merchantNames: merchants,
        keyword,
        types: [],
        statuses: [],
      },
    }),
    ...FETCH_CONFIG,
    context: 'searchElements',
  });
  return result.data || [];
}

// Step-2 enrichment: fetch full form records by name (returns objects with elementID, formName, formType, active)
export async function fetchFormsByNames(merchants, formNames, limit = 5) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/forms`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'forms',
      filters: {
        merchantNames: merchants,
        formNames,
        limit,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
        types: [],
        statuses: [],
      },
    }),
    ...FETCH_CONFIG,
    context: 'fetchFormsByNames',
  });
  return result.data || [];
}

// Step-2 enrichment: fetch full element records by name (returns objects with elementID, elementName, elementType, active)
export async function fetchElementsByNames(merchants, elementNames, limit = 5) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/elements`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'elements',
      filters: {
        merchantNames: merchants,
        elementNames,
        limit,
        types: [],
        statuses: [],
      },
    }),
    ...FETCH_CONFIG,
    context: 'fetchElementsByNames',
  });
  return result.data || [];
}

// Step-2 enrichment: fetch recent transactions for specific contactIDs
export async function fetchRecentTransactions(
  merchants,
  contactIDs,
  limit = 3,
  { sortField = 'timestamp', sortOrder = 'desc' } = {},
) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/transactions`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'transactions',
      filters: {
        merchantNames: merchants,
        contactIDs,
        limit,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
        startDate: '',
        endDate: '',
        paymentMethods: '',
        states: [],
        sortField,
        sortOrder,
        names: [],
        formNames: [],
        fundNames: [],
        ticketNames: [],
        frequencies: [],
        formTypes: [],
        amountType: 'AMOUNT',
      },
    }),
    ...FETCH_CONFIG,
    context: 'fetchRecentTransactions',
  });
  return result.data || [];
}

// Deterministic lifecycle summary for exact donation-history questions.
export async function fetchContactDonationSummary(
  merchants,
  contactIDs,
  states = ['SUCCEEDED', 'PARTIAL_REFUND'],
) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/transactions`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'contactDonationSummary',
      filters: {
        merchantNames: merchants,
        contactIDs,
        states,
      },
    }),
    ...FETCH_CONFIG,
    context: 'fetchContactDonationSummary',
  });
  return result.data || [];
}

// Step-2 enrichment: fetch subscriptions for specific contactIDs
export async function fetchContactSubscriptions(merchants, contactIDs, limit = 3) {
  const token = await getAuthToken();
  const result = await safeFetchJSON(`${OPENSEARCH_URL}/subscriptions`, {
    method: 'POST',
    headers: { Authorization: token },
    body: JSON.stringify({
      query: 'subscriptions',
      filters: {
        merchantNames: merchants,
        contactIDs,
        limit,
        amountEquals: '-1',
        amountGreaterThan: '-1',
        amountLessThan: '-1',
        startDate: '',
        endDate: '',
        frequencies: '',
        states: [],
        names: [],
        formNames: [],
        fundNames: [],
        amountType: 'AMOUNT',
        dateType: '',
      },
    }),
    ...FETCH_CONFIG,
    context: 'fetchContactSubscriptions',
  });
  return result.data || [];
}
